import React from "react";

import { DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useFieldArray } from "react-hook-form";
import { v4 } from "uuid";

import { FieldErrors } from "src/api/types";
import { Junctions, Operators } from "src/clients/flow-api";
import { Button } from "src/design-system/Button";
import { Branch } from "src/ruleNodeV2Editor/Branch";
import { RuleNodeV2Form } from "src/ruleNodeV2Editor/types";

type Props = {
  immutable: boolean;
  fieldErrors: FieldErrors | undefined;
  hideActionButtons?: boolean;
  lazyInputs: boolean;
};

export const Branches: React.FC<Props> = ({
  immutable,
  fieldErrors,
  hideActionButtons,
  lazyInputs,
}) => {
  const {
    fields: branches,
    append,
    remove,
    move,
  } = useFieldArray<RuleNodeV2Form>({
    name: "branches",
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const fieldToMove = branches.findIndex((field) => field.id === active.id);
      const fieldToInsert = branches.findIndex((field) => field.id === over.id);
      if (fieldToMove !== -1 && fieldToInsert !== -1) {
        move(fieldToMove, fieldToInsert);
      }
    }
  };

  return (
    <>
      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext
          disabled={immutable}
          items={branches.map((param) => param.id)}
          strategy={verticalListSortingStrategy}
        >
          {branches.map((branch, index) => (
            <Branch
              key={branch.id}
              fieldErrors={fieldErrors}
              hideActionButtons={hideActionButtons}
              id={branch.id}
              immutable={immutable}
              index={index}
              lazyInputs={lazyInputs}
              onRemove={() => remove(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
      {!hideActionButtons && (
        <Button
          dataLoc="rule-node-v2-add-rule"
          isDisabled={immutable}
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() =>
            append({
              id: v4(),
              name: `Rule ${branches.length + 1}`,
              effects: [],
              junction: Junctions.AND,
              clauses: [
                {
                  id_left: v4(),
                  id_right: v4(),
                  left: "",
                  right: "",
                  operator: Operators.EQ,
                },
              ],
            })
          }
        >
          Add rule
        </Button>
      )}
    </>
  );
};
